import { useLogger } from '@cj4/react-logger'
import { Place } from '@velocity/ui'
import axios, { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'

import { useAppConfig } from '../../context/AppConfigContext/AppConfigContext'
import { useUxConfig } from '../../context/UxConfigContext/UxConfigContext'
import { SearchSuppliersData, SearchSuppliersRequest } from '../../types/api'
import { ErrorPayload } from '../../types/models'
import { OSMGeoCodeJSONRes } from '../../types/OSM'
import { useAxios } from '../useAxios'

export interface RequestData
  extends Omit<
    SearchSuppliersRequest,
    'latitude' | 'longitude' | 'countryCode'
  > {
  place: Place
}

export interface RequestState {
  suppliers?: SearchSuppliersData
  error?: AxiosError<ErrorPayload>
  loading: boolean
}

// A wrapper of the /suppliers request
export const useSuppliers = (): [RequestState, (data: RequestData) => void] => {
  const logger = useLogger()
  const [request, setRequestState] = useState<RequestState>({
    loading: false,
  })

  const {
    countryConfig: { countryCode },
    sfALD,
  } = useUxConfig()

  const {
    endpoints: { apiBase: baseURL, suppliers: suppliersEndpoint },
  } = useAppConfig()

  const [{ data, error, loading }, deferredCall] =
    useAxios<SearchSuppliersData>(
      { method: 'POST', baseURL, url: suppliersEndpoint },
      true,
    )

  useEffect(() => {
    setRequestState({ suppliers: data, error, loading })
  }, [data, error, loading])

  const fetchSuppliers = useCallback(
    async (data: RequestData) => {
      const { place, malfunctionCodes, serviceCodes, ...restRequestData } = data

      let sfALDLocation: SearchSuppliersRequest['location']
      if (sfALD) {
        sfALDLocation = {
          locationName: place.name,
          city: place.city,
          country: place.country,
          countryCode,
          street: place.streetName,
          houseNumber: place.streetNumber,
          postcode: place.postalCode,
        }

        if (!sfALDLocation.city || !sfALDLocation.postcode) {
          const OSMData = await axios.request<OSMGeoCodeJSONRes>({
            method: 'get',
            url: `https://nominatim.openstreetmap.org/reverse?format=geocodejson&addressdetails=1&lon=${data.place.longitude}
&lat=${data.place?.latitude}`,
          })

          const openStreetAddress =
            OSMData?.data.features?.[0].properties.geocoding

          sfALDLocation = {
            ...sfALDLocation,
            city: sfALDLocation.city || openStreetAddress?.city || '',
            postcode:
              sfALDLocation.postcode || openStreetAddress?.postcode || '',
          }
        }
      }

      const requestData: SearchSuppliersRequest = {
        ...restRequestData,
        countryCode,
        latitude: place.latitude,
        longitude: place.longitude,
        malfunctionCodes,
        serviceCodes,
        location: sfALDLocation,
      }

      deferredCall({ data: requestData }).catch(logger.error)
    },
    [deferredCall, logger.error, sfALD, countryCode],
  )

  return [request, fetchSuppliers]
}
